import { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/config";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { formatApiErrorDetail } from "@/utils/apiError";

export default function CustomerLogin() {
  const { t } = useLanguage();
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      navigate(data.role === "admin" ? "/admin" : data.role === "employee" ? "/employee" : "/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { name: form.name, email: form.email, password: form.password },
        { withCredentials: true }
      );
      setUser(data);
      toast.success(t.account.register + " ✓");
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <UserRound className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-heading text-xl font-bold">{t.account.myAccount}</h1>
            <p className="text-sm text-muted-foreground">{t.account.loginSubtitle}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2 rounded-lg bg-muted p-1">
          <button
            data-testid="customer-login-tab"
            onClick={() => { setTab("login"); setError(""); }}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${tab === "login" ? "bg-background shadow" : "text-muted-foreground"}`}
          >
            {t.account.login}
          </button>
          <button
            data-testid="customer-register-tab"
            onClick={() => { setTab("register"); setError(""); }}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${tab === "register" ? "bg-background shadow" : "text-muted-foreground"}`}
          >
            {t.account.register}
          </button>
        </div>

        {error && (
          <p data-testid="customer-auth-error" className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {tab === "login" ? (
          <form onSubmit={handleLogin} className="mt-6 grid gap-4" data-testid="customer-login-form">
            <div className="grid gap-2">
              <Label htmlFor="cust-email">{t.account.email}</Label>
              <Input id="cust-email" type="email" required value={form.email} onChange={update("email")} data-testid="customer-email-input" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cust-pass">{t.account.password}</Label>
              <Input id="cust-pass" type="password" required value={form.password} onChange={update("password")} data-testid="customer-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="gap-2" data-testid="customer-login-button">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t.account.login}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="mt-6 grid gap-4" data-testid="customer-register-form">
            <div className="grid gap-2">
              <Label htmlFor="reg-name">{t.account.name}</Label>
              <Input id="reg-name" required value={form.name} onChange={update("name")} data-testid="customer-name-input" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="reg-email">{t.account.email}</Label>
              <Input id="reg-email" type="email" required value={form.email} onChange={update("email")} data-testid="customer-reg-email-input" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="reg-pass">{t.account.password} (min 8)</Label>
              <Input id="reg-pass" type="password" required minLength={8} value={form.password} onChange={update("password")} data-testid="customer-reg-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="gap-2" data-testid="customer-register-button">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t.account.register}
            </Button>
          </form>
        )}

        <Link to="/" data-testid="customer-back-home-link" className="mt-6 block text-center text-sm text-muted-foreground hover:text-foreground">
          {t.admin.backHome}
        </Link>
      </div>
    </div>
  );
}
